// Simulation send history page. Chronological log of SEND_LOG events with
// multi-select sim / user / dept / location / device / status filters.
// Scope (metadata filters from the shell, incl. sim + user chips from the
// Simulations page) applies before any local filters, so drilled-in scopes
// arrive pre-applied; the status filter can additionally be seeded by the
// drill context (e.g. the Report Rate KPI lands here on Reported).
//
// Calculations: filter SEND_LOG by scope + filters, sort by sent_at DESC.
// The raw event pull is capped server-side at the most-recent 20k rows; the
// table says so when that cut falls inside the window (BD-1020).
import React, { useState, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { FilterChip } from './MetaNav';
import { motion } from 'motion/react';
import { SEND_LOG } from '../data';
import { logInPeriod } from '../hooks/useDashboardData';
import { useTenancySimEvents, capNote } from '../tenancyEvents';
import { tagsForSim } from '../tagAnalytics';
import { STATUS_META, STATUS_OPTIONS, fmtLocal } from '../sendLogFormat';
import DataTable from './DataTable';
import WidgetState from './WidgetState';

const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };

// Same scope, tag and search rules as the Simulations page, so the Send History
// card's "View all (N)" matches the rows here (BD-1003).
export function applyScopeToSendLog(log, scope, search = '') {
    const s = scope || {};
    const q = String(search || '').trim().toLowerCase();
    const tagScope = s.tag ? (Array.isArray(s.tag) ? s.tag : [s.tag]) : null;
    return log.filter((e) => {
        if (!isIn(e.simName,  s.sim))        return false;
        if (!isIn(e.dept,     s.department)) return false;
        if (!isIn(e.location, s.location))   return false;
        if (!isIn(e.city,     s.city))       return false;
        if (!isIn(e.country,  s.country))    return false;
        if (!isIn(e.company,  s.company))    return false;
        if (!isIn(e.state,    s.state))      return false;
        if (s.user) {
            const userVals = Array.isArray(s.user) ? s.user : [s.user];
            const full = `${e.firstName} ${e.lastName}`;
            if (!userVals.some((v) => e.email === v || full === v)) return false;
        }
        if (tagScope) {
            const simTags = tagsForSim(e.simName);
            if (!tagScope.some((t) => simTags.includes(t))) return false;
        }
        if (q && !`${e.firstName} ${e.lastName} ${e.email} ${e.simName} ${e.dept} ${e.location} ${e.city} ${e.country} ${e.state} ${e.company}`.toLowerCase().includes(q)) return false;
        return true;
    });
}

const EMPTY_LOG = [];

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// Matches the active-users status chip in UsersPage: 4px-radius chip with a
// leading colored dot rather than the old pill, so status reads consistently
// across the roster and the send log.
export function StatusBadge({ status }) {
    const m = STATUS_META[status] || STATUS_META.no_click;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: '6px', py: '1px', borderRadius: '4px',
            fontSize: 10, fontWeight: 600,
            bgcolor: m.bg, color: m.text,
            border: '1px solid', borderColor: m.border,
            whiteSpace: 'nowrap',
        }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: m.dot, flexShrink: 0 }} />
            {m.label}
        </Box>
    );
}

// Multi-select filter: empty selection means 'All'.
// Thin adapter over the shared FilterChip so the send-history filter boxes
// are the exact same control as the Department/Location dimension chips.
// Keeps the original FilterMulti API (array state in, array state out).
export function FilterMulti({ value, onChange, options, placeholder, getLabel }) {
    const toLabel = (o) => (getLabel ? getLabel(o) : String(o));
    const opts = (options || []).map((o) => ({ label: toLabel(o), value: toLabel(o), raw: o }));
    const byValue = new Map(opts.map((o) => [o.value, o.raw]));
    return (
        <FilterChip
            label={placeholder}
            options={opts}
            activeValues={(value || []).map(toLabel)}
            onApply={({ values }) => onChange(values.map((v) => byValue.get(v)).filter((v) => v !== undefined))}
        />
    );
}

// The canonical send-log column set, shared with the Sim Detail recipients
// table so the two stay aligned. includeSim drops the Simulation column when
// every row is already scoped to one sim.
export function sendLogColumns({ includeSim = true } = {}) {
    return [
        { id: 'name',       label: 'Name',              always: true,    sortable: true, type: 'string', accessor: (e) => `${e.firstName} ${e.lastName}` },
        { id: 'email',      label: 'Email',             defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.email },
        ...(includeSim ? [{ id: 'simName', label: 'Simulation', defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.simName }] : []),
        { id: 'status',     label: 'Status',            defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.status,
          render: (e) => <StatusBadge status={e.status} /> },
        // Event timestamps sort on the raw ISO stamp and render in local time.
        { id: 'sent',       label: 'Sent',              defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.sentAt || '',     render: (e) => fmtLocal(e.sentAt) },
        { id: 'clicked',    label: 'Clicked',           defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.clickedAt || '',  render: (e) => fmtLocal(e.clickedAt) },
        { id: 'landed',     label: '2nd Stage Clicked', defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.landedAt || '',   render: (e) => fmtLocal(e.landedAt) },
        { id: 'reported',   label: 'Reported',          defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.reportedAt || '', render: (e) => fmtLocal(e.reportedAt) },
        { id: 'device',     label: 'Device',     sortable: true, type: 'string', accessor: (e) => e.device || '' },
        // Metadata: available via the column picker, off by default.
        { id: 'dept',       label: 'Department', sortable: true, type: 'string', accessor: (e) => e.dept },
        { id: 'location',   label: 'Location',   sortable: true, type: 'string', accessor: (e) => e.location },
        { id: 'city',       label: 'City',       sortable: true, type: 'string', accessor: (e) => e.city },
        { id: 'country',    label: 'Country',    sortable: true, type: 'string', accessor: (e) => e.country },
        { id: 'state',      label: 'State',      sortable: true, type: 'string', accessor: (e) => e.state },
        { id: 'company',    label: 'Company',    sortable: true, type: 'string', accessor: (e) => e.company },
        { id: 'adGroup',    label: 'Group',      sortable: true, type: 'string', accessor: (e) => (Array.isArray(e.adGroups) ? e.adGroups.join(', ') : e.adGroup) },
    ];
}


// Re-exported so existing imports of these names keep resolving here.
export { STATUS_META, STATUS_OPTIONS, fmtLocal };

export default function SendHistoryPage({ drill, scope, selfScope, search = '', initialStatus, period, startDate, endDate }) {
    // Seeded by drill context (e.g. the Report Rate KPI lands here with
    // status=reported); fully editable after landing.
    const [filterStatuses, setFilterStatuses] = useState(initialStatus ? [initialStatus] : []);
    const [filterDevices, setFilterDevices] = useState([]);

    const tenancyEvents = useTenancySimEvents({ scope, selfScope, period, startDate, endDate });
    const sourceLog = tenancyEvents.scoped ? (tenancyEvents.rows || EMPTY_LOG) : SEND_LOG;
    const scopedLog = useMemo(
        () => applyScopeToSendLog(logInPeriod(sourceLog, { period, startDate, endDate }), scope, search),
        [sourceLog, scope, search, period, startDate, endDate],
    );

    const devices = useMemo(() => [...new Set(scopedLog.map((e) => e.device).filter(Boolean))].sort(), [scopedLog]);

    const rows = useMemo(() => scopedLog.filter((e) => {
        if (filterStatuses.length  && !filterStatuses.includes(e.status))                return false;
        if (filterDevices.length   && !filterDevices.includes(e.device))                 return false;
        return true;
    }), [scopedLog, filterStatuses, filterDevices]);

    const hasFilters = filterStatuses.length || filterDevices.length;

    const columns = sendLogColumns();

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Filters sit above the table on their own line, same chip control and
                placement as every other page, rather than inside the table header.
                Simulation, user, department and location are the shared chips
                above (BD-1112); these are only the log's own fields. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <FilterMulti value={filterDevices}   onChange={setFilterDevices}   options={devices}   placeholder="All devices" />
                    <FilterMulti value={filterStatuses}  onChange={setFilterStatuses}  options={STATUS_OPTIONS} placeholder="All statuses" getLabel={(s) => STATUS_META[s]?.label || s} />
                {hasFilters ? (
                    <Button size="small" onClick={() => { setFilterStatuses([]); setFilterDevices([]); }}
                        sx={{ fontSize: 10, color: 'text.disabled', textTransform: 'none', minWidth: 0, px: 1, '&:hover': { color: 'text.secondary' } }}>
                        Clear
                    </Button>
                ) : null}
            </Box>
            <motion.div variants={fadeUp}>
                <WidgetState queries={[tenancyEvents]}>
                <DataTable
                    title="Simulation Send History"
                    subtitle={capNote(sourceLog, { period, startDate, endDate })}
                    columns={columns}
                    rows={rows}
                    rowKey={(e) => `${e.email}-${e.simName}-${e.sentAt}`}
                    onRowClick={(e) => drill('user-detail', `${e.firstName} ${e.lastName}`, { type: 'user', value: e.email })}
                    initialSort={{ by: 'sent', dir: 'desc' }}
                    storageKey="rbx-send-history-cols-v2"
                    emptyMessage="No matching delivery events"
                />
                </WidgetState>
            </motion.div>
        </motion.div>
    );
}
